// « Tarif du jour » — the view the operator asked for, kept pure.
//
//   « we define the setParameters, and from it i can see the day rate for each
//     plan and each channel (OTA + direct) »
//
// One date, one category: every parent plan across every channel, with the
// direct quote beside it at a few stay lengths.
//
// ── The OTA columns will read IDENTICALLY, and that is a measured fact ─────
//
// R15 established that rates do NOT vary by channel — what varies is
// restrictions (min LOS, stop sell), which is the entire reason per-channel
// plans exist at all. Rendering the columns anyway is what makes that visible
// instead of assumed: the day Booking.com is priced differently, this is the
// screen that shows it is not happening yet. Collapsing them into one column
// would hide the very property the design depends on.
//
// Direct differs for two independent reasons — the length-of-stay ladder, and
// `mealsDirect` (R12).

import {
  directRate,
  mealComponent,
  periodOn,
  type CalendarPeriod,
  type Cents,
  type IsoDate,
  type RatePlan,
  type RateProfile,
  type RoomType,
  type SeasonCalendar,
} from '@lemontaigu/pms-core';

export interface DayChannelCell {
  /** `null` for the direct column — a direct booking has no channel. */
  channelCode: string | null;
  label: string;
  priceCents: Cents;
  /** Only set on direct columns: how much under the OTA rate this is. */
  savingCents?: Cents;
}

export interface DayPlanRow {
  planId: number;
  planCode: string;
  planLabel: string;
  board: RatePlan['board'];
  /** Accommodation only — base + markup, before meals. Shown so the meal
   *  component is legible rather than baked in. */
  roomCents: Cents;
  mealsCents: Cents;
  cells: DayChannelCell[];
}

export interface DayMatrix {
  period: CalendarPeriod | null;
  closed: boolean;
  rows: DayPlanRow[];
  /** The stay lengths the direct columns quote. */
  nights: number[];
}

/** Stay lengths worth seeing side by side: one night, and the rungs of the
 *  ladder. Not configurable on purpose — this is a reading of the ladder, and a
 *  control for it would be a second place to express the same thing. */
export function directNightsFor(profile: RateProfile): number[] {
  const rungs = profile.lengthOfStayTiers.map((t) => t.minNights).sort((a, b) => a - b);
  return [1, ...rungs];
}

export function dayMatrix(
  calendar: SeasonCalendar,
  profile: RateProfile,
  plans: RatePlan[],
  roomType: RoomType,
  date: IsoDate,
): DayMatrix {
  const period = periodOn(calendar, date);
  const closed = calendar.closures.some((c) => date >= c.from && date <= c.to);
  const nights = directNightsFor(profile);

  if (period === null) return { period: null, closed, rows: [], nights };

  const rt = profile.roomTypes[roomType.id];
  if (rt === undefined) return { period, closed, rows: [], nights };

  const parents = plans.filter((p) => p.derivesFromId === null);
  const rows: DayPlanRow[] = parents.map((plan) => {
    const roomCents = period.baseCents + rt.markup[period.season];
    const mealsCents = mealComponent(plan.board, roomType.maxOccupancy, profile.mealsBooking);
    const ota = roomCents + mealsCents;

    // One column per channel that actually carries this plan — read from the
    // CHILDREN rather than from a channel list, so a plan not distributed to a
    // channel simply has no column instead of an invented one.
    const channels = plans.filter((p) => p.derivesFromId === plan.id);
    const cells: DayChannelCell[] = channels.map((c) => ({
      channelCode: c.channelCode,
      label: c.channelCode ?? c.code,
      priceCents: ota,
    }));

    for (const n of nights) {
      const direct = directRate(profile, period, roomType, plan.board, n);
      if (direct === null) continue;
      cells.push({
        channelCode: null,
        label: n === 1 ? 'Direct' : `Direct ${n} nuits`,
        priceCents: direct,
        savingCents: (ota - direct) as Cents,
      });
    }

    return {
      planId: plan.id,
      planCode: plan.code,
      planLabel: plan.labelFr,
      board: plan.board,
      roomCents,
      mealsCents,
      cells,
    };
  });

  return { period, closed, rows, nights };
}

// ── Formatting, at the screen's edge ─────────────────────────────────────────

export const fmtCents = (c: number): string => (c / 100).toFixed(2).replace('.', ',');

/** « 101,00 » / « 101 » / « 101.5 » → cents. Null on anything else, so a typo
 *  leaves the field alone rather than zeroing a price. */
export function parseCents(input: string): number | null {
  const cleaned = input.trim().replace(/\s/g, '').replace(',', '.');
  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export const fmtPct = (fraction: number): string =>
  (Math.round(fraction * 1000) / 10).toString().replace('.', ',');

/** Refuses a NEGATIVE percentage — « -7 » would silently become a surcharge. */
export function parsePct(input: string): number | null {
  const cleaned = input.trim().replace(/\s/g, '').replace(',', '.').replace('%', '');
  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (n < 0 || n > 100) return null;
  return Math.round(n * 10) / 1000;
}

export function parseNights(input: string): number | null {
  const cleaned = input.trim();
  if (!/^\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return n >= 1 ? n : null;
}

/**
 * Why a period's dates cannot be saved, in French — or null when they can.
 *
 * Three refusals the database would also make, said BEFORE the round trip so the
 * operator reads a sentence rather than a constraint name:
 *  - a malformed or backwards range (`pms_season_period_range_chk`);
 *  - a range outside the fiscal year, which no grid would ever read;
 *  - a start date another period already uses (`pms_season_period_start_uq`).
 *
 * Overlap is deliberately NOT refused: the brief layers event periods (the Tour
 * de France) on top of a season, and `validateCalendar` already warns about it.
 */
export function periodDateProblem(
  range: { from: string; to: string },
  fiscal: { from: IsoDate; to: IsoDate },
  otherStarts: readonly string[],
): string | null {
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (!iso.test(range.from) || !iso.test(range.to)) return 'Dates incomplètes.';
  if (range.to < range.from) return 'La fin précède le début.';
  if (range.from < fiscal.from || range.to > fiscal.to) {
    return `La période doit rester dans l’exercice (${fiscal.from} → ${fiscal.to}).`;
  }
  if (otherStarts.includes(range.from)) return 'Une autre période commence déjà à cette date.';
  return null;
}
